
import React from 'react';
import { SEO } from '@/components/SEO';
import { StructuredData } from './StructuredData';
import { BreadcrumbItem } from '@/components/navigation/BreadcrumbNav';
import { 
  createTouristDestinationSchema,
  createRestaurantSchema,
  createHotelSchema,
  createPersonSchema,
  createWebSiteSchema,
  createOrganizationSchema
} from './StructuredData';

interface EnhancedSEOProps {
  title?: string;
  description?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article';
  publishedTime?: string;
  modifiedTime?: string;
  author?: string;
  noIndex?: boolean;
  keywords?: string[];
  canonical?: string;
  breadcrumbs?: BreadcrumbItem[];
  enableSearchBox?: boolean;
  
  // Schema.org data
  schemaType?: 'destination' | 'restaurant' | 'hotel' | 'person' | 'attraction' | 'landmark';
  schemaData?: {
    name: string;
    description?: string;
    image?: string;
    latitude?: number;
    longitude?: number;
    country: string;
    city?: string;
    address?: string;
    rating?: number;
    reviewCount?: number;
    priceRange?: string;
    cuisineTypes?: string[];
    touristTypes?: string[];
    jobTitle?: string;
    languages?: string[];
    amenities?: {name: string; value?: boolean | string}[];
    reviews?: Array<{rating: number; author: string}>;
    aggregateRating?: {value: number; count: number};
  };
}

export const EnhancedSEO: React.FC<EnhancedSEOProps> = ({
  schemaType,
  schemaData,
  enableSearchBox = false,
  image = 'https://images.unsplash.com/photo-1506905925346-21bda4d32df4?w=1200&h=630&fit=crop&auto=format&q=80', // Beautiful travel landscape as default
  ...seoProps
}) => {
  // Create schema markup based on type
  const createSchemaMarkup = () => {
    const schemas = [];
    
    // Always include website and organization schema on the homepage
    if (enableSearchBox) {
      schemas.push(createWebSiteSchema());
      schemas.push(createOrganizationSchema());
    }
    
    // Add specific schema based on content type
    if (schemaType && schemaData) {
      const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
      const fullImageUrl = schemaData.image?.startsWith('http') 
        ? schemaData.image 
        : (image.startsWith('http') ? image : `${typeof window !== 'undefined' ? window.location.origin : ''}${image}`);
      
      switch (schemaType) {
        case 'destination':
          schemas.push(createTouristDestinationSchema({
            ...schemaData,
            url: currentUrl,
            image: fullImageUrl
          }));
          break;
          
        case 'restaurant':
          schemas.push(createRestaurantSchema({
            ...schemaData,
            url: currentUrl,
            image: fullImageUrl,
            // Convert amenities to string array for restaurants
            servesCuisine: schemaData.cuisineTypes
          }));
          break;
          
        case 'hotel':
          schemas.push(createHotelSchema({
            ...schemaData,
            url: currentUrl,
            image: fullImageUrl,
            // Convert amenities to string array for hotels
            amenities: schemaData.amenities?.map(amenity => amenity.name) || []
          }));
          break;
          
        case 'person':
          schemas.push(createPersonSchema({
            ...schemaData,
            url: currentUrl,
            image: fullImageUrl
          }));
          break;
          
        case 'attraction':
        case 'landmark':
          // For attractions and landmarks, we can use TouristDestination or create specific schema
          schemas.push(createTouristDestinationSchema({
            ...schemaData,
            url: currentUrl,
            image: fullImageUrl,
            touristTypes: schemaType === 'landmark' ? ['Historical', 'Cultural'] : schemaData.touristTypes
          }));
          break;
      }
    }
    
    return schemas;
  };

  const schemas = createSchemaMarkup();

  return (
    <>
      <SEO {...seoProps} image={image} enableSearchBox={enableSearchBox} />
      {schemas.length > 0 && <StructuredData schema={schemas} />}
    </>
  );
};
